import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  CarePlanTabs,
  getCarePlanSegment,
  isCarePlanTabAvailable,
  getApiErrorMessage,
  getFileUrl,
  NO_CAREPLAN_PROGRAM,
  LEGACY_CAREPLAN_ENABLED,
} from "./constant";
import { Medications } from "../medications/medications";
import {
  useGetActiveProgramsQuery,
  useGetCarePlanDataQuery,
  useGetCarePlanQuery,
  useGetCarePlanProblemQuery,
  carePlanApi,
} from "../../redux/service/careplan_service";
import LoadingBar from "../../components/LoadingBar";
import FullScreenLoader from "../../components/FullScreenLoader";
import { Icon } from "../../utils/icons";
import { getLoggedinUser } from "../../userStore";
import {
  SOUTHSIDE_MEDICAL_CENTER_ID,
  FOUR_CORNERS_PRIMARY_CARE_CENTERS_ID,
  ETHIZO_PRACTICE_ID,
} from "../../constants/practiceIds";
import ReloadIcon from "../../components/Icons/ReloadIcon";
import CareplanRecommendVisitFrequencies from "./careplan-components/careplan_recommend_visti_frequencies";
import CareplanProblems from "./careplan-components/careplan_problems";
import CareplanAllergies from "./careplan-components/careplan_allergies";
import CareplanTrackers from "./careplan-components/careplan_trackers";
import CareplanGoals from "./careplan-components/careplan_goals";
import CareplanBarriers from "./careplan-components/careplan_barriers";
import CareplanInterventions from "./careplan-components/careplan_interventions";
import CareplanSupport from "./careplan-components/careplan_support";
import CareplanScreening from "./careplan-components/careplan_screening";
import CareplanEvents from "./careplan-components/careplan_events";
import CareplanGuidelines from "./careplan-components/careplan_guidelines";

function CarePlan() {
  const user = getLoggedinUser();

  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedSection, setSelectedSection] = useState(CarePlanTabs[0].type);
  const isLegacyWorkflow =
    LEGACY_CAREPLAN_ENABLED && selectedProgram === NO_CAREPLAN_PROGRAM.slug;
  const { data: carePlanData, refetch: refetchCarePlanData, isLoading: isLoadingCarePlan, isFetching: isFetchingCarePlan, isError: isCarePlanError, error: carePlanError } =
    useGetCarePlanDataQuery(
      { slug: selectedProgram },
      { skip: !selectedProgram || isLegacyWorkflow }
    );
  const {
    data: legacyCarePlan,
    refetch: refetchLegacyCarePlan,
    isLoading: isLoadingLegacyCarePlan,
    isFetching: isFetchingLegacyCarePlan,
    isError: isLegacyCarePlanError,
    error: legacyCarePlanError,
  } = useGetCarePlanQuery({ skip_encounter: true }, { skip: !isLegacyWorkflow });
  const {
    data: legacyProblems,
    refetch: refetchLegacyProblems,
    isLoading: isLoadingLegacyProblems,
    isFetching: isFetchingLegacyProblems,
    isError: isLegacyProblemsError,
    error: legacyProblemsError,
  } = useGetCarePlanProblemQuery({ skip_encounter: true }, { skip: !isLegacyWorkflow });
  const segments = useMemo(
    () => carePlanData?.data?.segments || [],
    [carePlanData?.data?.segments]
  );
  const segmentData = useMemo(
    () => ({
      problems: getCarePlanSegment(segments, "problems"),
      visit_frequencies: getCarePlanSegment(segments, "visit_frequencies"),
      allergies: getCarePlanSegment(segments, "allergies"),
      medications: getCarePlanSegment(segments, "medications"),
      trackers: getCarePlanSegment(segments, "trackers"),
      goals: getCarePlanSegment(segments, "goals"),
      barriers: getCarePlanSegment(segments, "barriers"),
      labs: getCarePlanSegment(segments, "labs"),
      radiology: getCarePlanSegment(segments, "radiology"),
      education: getCarePlanSegment(segments, "education"),
      other_interventions: getCarePlanSegment(segments, "other_interventions"),
      screenings: getCarePlanSegment(segments, "screenings"),
      events: getCarePlanSegment(segments, "events"),
      guidelines: getCarePlanSegment(segments, "guidelines"),
      support: getCarePlanSegment(segments, "support", {}),
    }),
    [segments]
  );
  const legacySegmentData = useMemo(() => {
    const data = legacyCarePlan?.data || {};
    return {
      problems: legacyProblems?.data || [],
      visit_frequencies: [],
      allergies: data.allergy || [],
      medications: data.medication || [],
      trackers: data.trackers || [],
      goals: data.goals || [],
      barriers: data.barriers || [],
      labs: data.lab_orders || [],
      radiology: data.radiology || [],
      education: [],
      other_interventions: data.other_interventions || [],
      screenings: data.Screening || [],
      events: data.event || [],
      guidelines: data.guidelines || [],
      support: data.support || {},
    };
  }, [legacyCarePlan, legacyProblems]);
  const sectionData = isLegacyWorkflow ? legacySegmentData : segmentData;
  const { data: activeProgramsData, refetch: refetchActivePrograms, isLoading: isLoadingPrograms, isFetching: isFetchingPrograms, isError: isProgramsError, error: programsError } =
    useGetActiveProgramsQuery({});
  const programsList = useMemo(
    () =>
      LEGACY_CAREPLAN_ENABLED
        ? [NO_CAREPLAN_PROGRAM, ...(activeProgramsData?.data || [])]
        : activeProgramsData?.data || [],
    [activeProgramsData?.data]
  );
  const [printCarePlanTrigger] =
    carePlanApi.endpoints.printCareplan.useLazyQuery();

  const [loadingListingData, setLoadingListingData] = useState(false);
  const [loadingPrintData, setLoadingPrintData] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const hasProgramsError =
    isProgramsError ||
    (activeProgramsData?.code != null && activeProgramsData.code !== 0);
  const hasCarePlanError =
    !!selectedProgram &&
    !isLegacyWorkflow &&
    (isCarePlanError ||
      (carePlanData?.code != null && carePlanData.code !== 0));
  const hasLegacyError =
    isLegacyWorkflow &&
    (isLegacyCarePlanError ||
      isLegacyProblemsError ||
      (legacyCarePlan?.code != null && legacyCarePlan.code !== 0) ||
      (legacyProblems?.code != null && legacyProblems.code !== 0));
  const hasSectionError = hasCarePlanError || hasLegacyError;

  const handlePrint = () => {
    if (!selectedProgram || hasSectionError) {
      return;
    }
    setLoadingPrintData(true);
    printCarePlanTrigger({ type: "careplan", encounter: user?.encounter, width: 600 })
      .then((res) => {
        if (res?.error) {
          return;
        }
        const fileId = res?.data?.data;
        if (fileId) {
          window.open(getFileUrl(fileId), "_blank");
        }
      })
      .finally(() => {
        setLoadingPrintData(false);
      });
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setLoadingListingData(true);
    Promise.all([
      refetchActivePrograms(),
      isLegacyWorkflow
        ? Promise.all([refetchLegacyCarePlan(), refetchLegacyProblems()])
        : selectedProgram
        ? refetchCarePlanData()
        : Promise.resolve(),
    ]).finally(() => {
      setRefreshing(false);
      setLoadingListingData(false);
    });
  };

  const isAllowedPracticeForVisitFrequencies =
    user?.practice_id?.toString() === SOUTHSIDE_MEDICAL_CENTER_ID.toString() ||
    user?.practice_id?.toString() === ETHIZO_PRACTICE_ID.toString() ||
    user?.practice_id?.toString() === FOUR_CORNERS_PRIMARY_CARE_CENTERS_ID.toString();

  const sectionTabs = useMemo(
    () =>
      isAllowedPracticeForVisitFrequencies
        ? CarePlanTabs
        : CarePlanTabs.filter((tab) => tab.type !== "visit_frequencies"),
    [isAllowedPracticeForVisitFrequencies]
  );

  const displaySectionTabs = useMemo(() => {
    if (isLegacyWorkflow) {
      return sectionTabs;
    }
    return sectionTabs.filter((tab) => isCarePlanTabAvailable(tab.type, segments));
  }, [sectionTabs, segments, isLegacyWorkflow]);

  const selectedSectionRef = useRef(selectedSection);
  const hasInitializedProgramRef = useRef(false);

  selectedSectionRef.current = selectedSection;

  useEffect(() => {
    if (!programsList.length || hasInitializedProgramRef.current) {
      return;
    }
    hasInitializedProgramRef.current = true;
    setSelectedProgram(programsList[0].slug);
  }, [programsList]);

  useEffect(() => {
    const isValidSection = displaySectionTabs.some(
      (tab) => tab.type === selectedSectionRef.current
    );
    if (!isValidSection) {
      setSelectedSection(displaySectionTabs[0]?.type || CarePlanTabs[0].type);
    }
  }, [displaySectionTabs]);

  const isPageLoading =
    isLoadingPrograms ||
    (isLegacyWorkflow
      ? isLoadingLegacyCarePlan || isLoadingLegacyProblems
      : isLoadingCarePlan);
  const isPageFetching = isFetchingPrograms || isFetchingCarePlan;
  const isCarePlanDataLoading = isLoadingCarePlan || isFetchingCarePlan;
  const isLegacyDataLoading =
    isLoadingLegacyCarePlan ||
    isFetchingLegacyCarePlan ||
    isLoadingLegacyProblems ||
    isFetchingLegacyProblems;
  const isProgramDataLoading = isLegacyWorkflow
    ? isLegacyDataLoading
    : isCarePlanDataLoading;
  const isContentLoading =
    isPageLoading ||
    isPageFetching ||
    (isLegacyWorkflow ? isLegacyDataLoading : isCarePlanDataLoading) ||
    loadingListingData;

  const programsErrorMessage = getApiErrorMessage(
    programsError,
    activeProgramsData,
    "Unable to load care plan programs."
  );
  const carePlanErrorMessage = isLegacyWorkflow
    ? getApiErrorMessage(
        legacyCarePlanError || legacyProblemsError,
        legacyCarePlan?.code !== 0 ? legacyCarePlan : legacyProblems,
        "Unable to load care plan data."
      )
    : getApiErrorMessage(
        carePlanError,
        carePlanData,
        "Unable to load care plan data."
      );

  const renderStatusMessage = (message, type = "empty", showRetry = false) => (
    <div
      className={`rounded-lg border px-4 py-8 text-center text-sm shadow-sm mt-3 ${
        type === "error"
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-gray-200 bg-white text-gray-500"
      }`}
    >
      <p>{message}</p>
      {showRetry && (
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="mt-4 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          Try again
        </button>
      )}
    </div>
  );

  const renderSectionContent = () => {
    switch (selectedSection) {
      case "problems":
        return <CareplanProblems listData={sectionData.problems} loading={isContentLoading} />;
      case "visit_frequencies":
        return (
          <CareplanRecommendVisitFrequencies
            listData={sectionData.visit_frequencies}
            loading={isContentLoading}
            legacyMode={isLegacyWorkflow}
          />
        );
      case "allergies":
        return <CareplanAllergies listData={sectionData.allergies} loading={isContentLoading} />;
      case "medication":
        return (
          <Medications
            header={false}
            dataMeds={sectionData.medications}
            loading={isContentLoading}
            legacyMode={isLegacyWorkflow}
          />
        );
      case "trackers":
        return <CareplanTrackers listData={sectionData.trackers} loading={isContentLoading} />;
      case "goals":
        return <CareplanGoals listData={sectionData.goals} loading={isContentLoading} />;
      case "barriers":
        return <CareplanBarriers listData={sectionData.barriers} loading={isContentLoading} />;
      case "interventions":
        return (
          <CareplanInterventions
            labsData={sectionData.labs}
            radiologyData={sectionData.radiology}
            educationData={sectionData.education}
            otherInterventionsData={sectionData.other_interventions}
            loading={isContentLoading}
            legacyMode={isLegacyWorkflow}
          />
        );
      case "support":
        return <CareplanSupport supportData={sectionData.support} loading={isContentLoading} />;
      case "screening":
        return <CareplanScreening listData={sectionData.screenings} loading={isContentLoading} />;
      case "events":
        return <CareplanEvents listData={sectionData.events} loading={isContentLoading} />;
      case "guidelines":
        return <CareplanGuidelines listData={sectionData.guidelines} loading={isContentLoading} />;
      default:
        return null;
    }
  };

  return (
    <>
      {isPageLoading && <FullScreenLoader />}
      <div className="flex border-gray-300 bg-gray-200 px-3 py-3 sm:px-4 flex-wrap items-center justify-between sm:flex-nowrap">
        <div className="flex gap-5 items-center">
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            Care Plan
          </h3>
        </div>
        <div className="flex flex-shrink-0">
          <button
            onClick={handlePrint}
            disabled={!selectedProgram || hasSectionError || loadingPrintData}
            className="mr-2 cursor-pointer relative inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Print
            {loadingPrintData && (
              <span className="pl-3">
                <Icon type="inline-loader" />
              </span>
            )}
          </button>
          <button
            className="rounded bg-white px-2 py-1 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <ReloadIcon className={`w-5 h-5 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
      {(isContentLoading || loadingPrintData) && (
        <div className="mb-3 bg-white -mx-4">
          <LoadingBar />
        </div>
      )}

      <div className="flex-auto bg-white justify-start text-start">
        {hasProgramsError ? (
          <div className="p-3">
            {renderStatusMessage(programsErrorMessage, "error", true)}
          </div>
        ) : (
          programsList.length > 0 && (
          <div className="border-b border-gray-200 bg-gray-50 px-3 pt-3">
            <nav className="flex flex-wrap gap-2 pb-3" aria-label="Care Plan Programs">
              {programsList.map((program) => {
                const isSelectedProgram = selectedProgram === program.slug;
                const isProgramTabDisabled =
                  isProgramDataLoading && !isSelectedProgram;

                return (
                <button
                  key={program.slug}
                  type="button"
                  onClick={() => setSelectedProgram(program.slug)}
                  disabled={isProgramTabDisabled}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    isSelectedProgram
                      ? "bg-indigo-600 text-white shadow-sm"
                      : isProgramTabDisabled
                      ? "cursor-not-allowed bg-white text-gray-400 ring-1 ring-inset ring-gray-300"
                      : "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {program.title}
                </button>
                );
              })}
            </nav>
          </div>
          )
        )}

        {!hasProgramsError && programsList.length > 0 && displaySectionTabs.length > 0 && (
        <div className="border-b border-gray-200 px-3">
          <nav className="-mb-px flex overflow-x-auto pb-0 gap-[10px]" aria-label="Care Plan Sections">
            {displaySectionTabs.map((tab) => (
              <button
                key={tab.type}
                type="button"
                onClick={() => setSelectedSection(tab.type)}
                disabled={isContentLoading || hasSectionError}
                className={`whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                  selectedSection === tab.type
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                }`}
              >
                {tab.title}
              </button>
            ))}
          </nav>
        </div>
        )}

        <div className="p-3">
          {hasSectionError
            ? renderStatusMessage(carePlanErrorMessage, "error", true)
            : !selectedProgram
            ? renderStatusMessage("Select a care plan program to view details.")
            : renderSectionContent()}
        </div>
      </div>
    </>
  );
}

export default CarePlan;
